/**
 * What may be uploaded, and how big — one copy for the API and the React
 * build (CommonJS, like utils/engagementName).
 *
 * Before this existed the same rule was written twice and drifted: the page
 * put `.xls` in its `accept`, the route's regex did not, and the file went
 * into the field fine and came back a 400 on submit. Every FilePicker and
 * every multer config names a kind from this table instead of spelling the
 * list out again.
 *
 * A kind says what the FILE may be. How MANY files a field takes is the
 * field's own business (a CWCC request allows 10 attachments, a QC record
 * 20), so it stays with the route and the call site.
 *
 * Adding a kind: add the row, use it on both sides. Widening an existing kind
 * widens every surface that names it — that is the point, but check that it
 * is what you mean.
 */

const MB = 1024 * 1024;

// `jpeg` rides along with `jpg` everywhere; printing both in a hint reads as
// two formats.
const LABEL_SKIP = new Set(['jpeg']);

const UPLOAD_KINDS = {
  // Supporting attachments on a request or a record — the common case.
  docsAndImages: { ext: ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg'], maxBytes: 30 * MB },
  // The same, where a working paper or a schedule is legitimately a spreadsheet
  // (Registration, Screening, QC Addendum, engagement master attachments).
  docsSheetsImages: { ext: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg'], maxBytes: 30 * MB },
  // A signed document, never an image — Signed Financial Statements.
  docsOnly: { ext: ['pdf', 'doc', 'docx'], maxBytes: 30 * MB },
  // BizFile, screening report: PDF or nothing. Checked by extension, never by
  // the declared MIME type (workflow-contract.md 3.1).
  pdfOnly: { ext: ['pdf'], maxBytes: 30 * MB },
  // CaseWare screenshots on QC35.
  images: { ext: ['png', 'jpg', 'jpeg', 'gif', 'webp'], maxBytes: 30 * MB },
  // QM registers also file the mail that raised the matter.
  registerFiles: { ext: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'msg', 'eml', 'png', 'jpg', 'jpeg'], maxBytes: 30 * MB },
  // Training handouts — slide decks belong here, and a deck is bigger.
  trainingMaterial: { ext: ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png'], maxBytes: 50 * MB },
  // Session recordings.
  recording: { ext: ['mp4', 'm4v', 'mov', 'webm'], maxBytes: 500 * MB },
  // Read in the browser, never uploaded: the quiz-question workbook.
  spreadsheet: { ext: ['xlsx', 'xlsm', 'csv'], maxBytes: 30 * MB },
};

const kindOf = (kind) => {
  const spec = UPLOAD_KINDS[kind];
  if (!spec) throw new Error(`Unknown upload kind "${kind}" — see src/utils/uploadKinds.js`);
  return spec;
};

/** `.pdf,.doc,.docx,.png,.jpg,.jpeg` — the <input accept> value. */
const acceptOf = (kind) => kindOf(kind).ext.map((e) => `.${e}`).join(',');

/** `/\.(pdf|doc|docx|png|jpg|jpeg)$/i` — the server-side gate. */
const extRegexOf = (kind) => new RegExp(`\\.(${kindOf(kind).ext.join('|')})$`, 'i');

const fitsKind = (kind, name) => extRegexOf(kind).test(String(name || ''));

const maxBytesOf = (kind) => kindOf(kind).maxBytes;

/** `30 MB`, `500 MB` — how the limit is written in hints and errors. */
const sizeLabelOf = (kind) => `${Math.round(kindOf(kind).maxBytes / MB)} MB`;

/** `PDF / DOC / DOCX / PNG / JPG` — how the formats are written in hints. */
const formatLabelOf = (kind) =>
  kindOf(kind).ext.filter((e) => !LABEL_SKIP.has(e)).map((e) => e.toUpperCase()).join(' / ');

/**
 * The hint under a file field: `PDF / DOC / DOCX, up to 30 MB per file.`
 * `multiple` adds "per file" — a single-file field saying "per file" invites
 * the question of which other file.
 */
const hintOf = (kind, { multiple = false } = {}) => {
  const formats = formatLabelOf(kind);
  const only = formats.includes('/') ? formats : `${formats} only`;
  return `${only}, up to ${sizeLabelOf(kind)}${multiple ? ' per file' : ''}.`;
};

/** `Only .pdf, .doc, .docx, .png and .jpg files are accepted` */
const rejectMessageOf = (kind) => {
  const list = kindOf(kind).ext.filter((e) => !LABEL_SKIP.has(e)).map((e) => `.${e}`);
  const last = list.pop();
  return list.length
    ? `Only ${list.join(', ')} and ${last} files are accepted`
    : `Only ${last} files are accepted`;
};

/** `"big.pdf" is 42 MB — the limit is 30 MB` */
const tooBigMessage = (kind, name, bytes) =>
  `"${name}" is ${Math.max(1, Math.round(bytes / MB))} MB — the limit is ${sizeLabelOf(kind)}`;

module.exports = {
  UPLOAD_KINDS,
  acceptOf,
  extRegexOf,
  fitsKind,
  maxBytesOf,
  sizeLabelOf,
  formatLabelOf,
  hintOf,
  rejectMessageOf,
  tooBigMessage,
};
